import {
  checkPolicyNumber,
  getCaseName,
  getPolicyNumber,
  getProductFamiliesByProductType,
  getSalaryDeductionBillingInfo,
  getUserAuthorizedInfo,
  getEnrollmentApplication,
  createPolicyNumber,
  getACHIndicator,
  getManagedStatus,
} from 'ApplicationEntry/api/awdApi';
import ApplicationEntryRouteConstants from 'ApplicationEntry/ApplicationEntryRouteConstants';
import {UserAssignedPolicyNumber} from 'ApplicationEntry/models/PolicyNumber';
import {LoadedPolicyTypes} from 'ApplicationEntry/models/PolicyTypes';
import {push} from 'connected-react-router';
import {AsyncResult, Loaded} from 'Core/models/Async';
import application, * as A from 'ApplicationEntry/ducks/application';
import * as CD from 'ApplicationEntry/ducks/caseDetails';
import * as E from 'fp-ts/lib/Either';
import {all, call, put, takeLatest} from 'redux-saga/effects';
import {ApplicationType} from 'ApplicationEntry/models/ApplicationType';
import * as BAActions from 'ApplicationEntry/ducks/billingAgent';
import * as PremiumActions from 'ApplicationEntry/ducks/premium';
import {Family, Product} from 'ApplicationEntry/models/ProductsRiders/PlanModel';
import {BillingMode} from './billingAgentSaga';

function* getProductsSaga() {
  yield takeLatest(CD.CaseDetailsActionType.GET_PRODUCT_FAMILIES, function*(action: ReturnType<typeof CD.getProducts>) {
    const {applicationType} = action.payload;

    let stateExclusivityIgnored = 'NY';
    let ignoreNonexclusive = false;
    let temporaryApplicationType = applicationType;

    if (temporaryApplicationType === ApplicationType.NewYork) {
      temporaryApplicationType = ApplicationType.Ordinary;
      stateExclusivityIgnored = 'NC,TN'; // Ignore products that are not exclusive to New York and have a known state exclusivity.
      ignoreNonexclusive = true; // Ignore products that do not have state exclusivity.
    }

    const response: AsyncResult<Family[]> = yield call(
      getProductFamiliesByProductType,
      temporaryApplicationType,
      stateExclusivityIgnored,
      ignoreNonexclusive
    );

    if (E.isRight(response)) {
      const products: Product[] = response.right.flatMap(family =>
        family.products!.map(product => {
          product.family = family;

          return product;
        })
      );

      yield put(CD.changeProductType(new LoadedPolicyTypes(products, [])));
    } else if (response.left.key === 'Unauthorized') {
      yield put(push(ApplicationEntryRouteConstants.AWDLogin));
    } else {
      yield put(CD.changeProductType(new LoadedPolicyTypes([], [])));
    }
  });
}

function* assignPolicyNumber() {
  yield takeLatest(CD.CaseDetailsActionType.ASSIGN_POLICY_NUMBER, function*(
    action: ReturnType<typeof CD.assignPolicyNumber>
  ) {
    const response: AsyncResult<{policyNumber: object}> = yield call(getPolicyNumber, action.payload.isElectonic);
    if (E.isRight(response)) {
      yield put(A.resetApplicationExceptCaseDetailsAndPolicyRecord());
      yield put(CD.changePolicyNumber(new Loaded(response.right.policyNumber['policyNumber'])));
      yield put(CD.checkPolicyNumberExists(false, ''));
    } else if (response.left.key === 'Unauthorized') {
      yield put(push(ApplicationEntryRouteConstants.AWDLogin));
    } else {
      yield put(CD.changePolicyNumber(response.left));
    }
  });
}

function* addPolicyNumber() {
  yield takeLatest(CD.CaseDetailsActionType.VALIDATE_POLICY_NUMBER, function*(
    action: ReturnType<typeof CD.validatePolicyNumber>
  ) {
    let policyNumber = '';
    for (let i = 0; i < action.payload.policyNumberArr.length; i++) {
      policyNumber =
        action.payload.policyNumberArr[i].companyCode +
        action.payload.policyNumber.value +
        action.payload.policyNumberArr[i].productCode;

      const responseLookup: AsyncResult<{enrollment: object}> = yield call(getEnrollmentApplication, policyNumber);

      if (E.isLeft(responseLookup) && responseLookup.left.key === 'NotFound') {
        let errDetails: [] = responseLookup.left.errorDetails?.messages?.filter(
          (message: {[x: string]: string | string[]}) =>
            message['responseSeverity'] == 'ERROR' && message['message'].includes('The policy could not be found')
        );
        if (errDetails?.length > 0) {
          yield put(CD.checkPolicyNumberExists(false, policyNumber));
        } else {
          yield put(CD.checkPolicyNumberExists(true, policyNumber));
        }
      } else if (E.isLeft(responseLookup) && responseLookup.left.key === 'Unauthorized') {
        yield put(push(ApplicationEntryRouteConstants.AWDLogin));
      } else {
        yield put(CD.checkPolicyNumberExists(true, policyNumber));

        yield put(CD.policyNumberError(policyNumber));
      }
    }

    let response: AsyncResult<{messages: object[]; statusString: string; policyNumber: object}> = yield call(
      checkPolicyNumber,
      action.payload.policyNumber.value
    );

    if (E.isLeft(response) && response.left.key === 'NotFound') {
      response = yield call(createPolicyNumber, action.payload.policyNumber.value);
    }

    if (E.isRight(response)) {
      response.right.policyNumber['status'] === 'Reserved'
        ? yield put(CD.insertPolicyNumber(UserAssignedPolicyNumber.create(response.right.policyNumber['policyNumber'])))
        : yield put(
            CD.insertPolicyNumber(UserAssignedPolicyNumber.create(response.right.policyNumber['policyNumber']))
          );
    } else if (response.left.key === 'Unauthorized') {
      yield put(push(ApplicationEntryRouteConstants.AWDLogin));
    } else {
      yield put(CD.policyNumberError(action.payload.policyNumber.value));
    }
  });
}

function* getSalaryDeductionBillingInfoSaga() {
  yield takeLatest(CD.CaseDetailsActionType.CHECK_CASE_NUMBER_BILLING, function*(
    action: ReturnType<typeof CD.checkCaseNumberBillingMode>
  ) {
    const response: AsyncResult<{
      saldBillingInfoDto: {
        billingMode: BillingMode;
        salaryDeductionBillingCode: string;
        situsState: string;
        caseName: string;
      };
    }> = yield call(getSalaryDeductionBillingInfo, action.payload.caseNumber);
    if (E.isRight(response)) {
      yield put(CD.asyncCaseNumber(new Loaded(action.payload.caseNumber)));
      yield put(CD.getBillingModefromCaseNumber(response.right.saldBillingInfoDto?.salaryDeductionBillingCode));
      yield put(CD.getSitusStatefromCaseNumber(response.right.saldBillingInfoDto.situsState));
      yield put(CD.changeCaseName(response.right?.saldBillingInfoDto?.caseName));

      if (response.right.saldBillingInfoDto?.salaryDeductionBillingCode === '4') {
        yield put(CD.getBillingModefromCaseNumber('Monthly'));
        yield put(BAActions.changeBillingMode('Monthly'));
        yield put(PremiumActions.changeBillMode('Monthly'));
      } else if (response.right.saldBillingInfoDto?.salaryDeductionBillingCode === '5') {
        yield put(CD.getBillingModefromCaseNumber('Weekly'));
        yield put(BAActions.changeBillingMode('Weekly'));
        yield put(PremiumActions.changeBillMode('Weekly'));
      }
    } else if (response.left.key === 'Unauthorized') {
      yield put(push(ApplicationEntryRouteConstants.AWDLogin));
    } else if (response.left.key === 'ServerError') {
      yield put(CD.asyncCaseNumber(new Loaded(action.payload.caseNumber)));
      yield put(CD.changeCaseNumberError(action.payload.caseNumber));
    } else {
      yield put(CD.changeCaseNumberError(action.payload.caseNumber));
    }
  });
}

function* getCaseNameSaga() {
  yield takeLatest(CD.CaseDetailsActionType.CHECK_CASE_NUMBER, function*(
    action: ReturnType<typeof CD.checkCaseNumber>
  ) {
    const response: AsyncResult<{caseName: string}> = yield call(getCaseName, action.payload.caseNumber);
    if (E.isRight(response)) {
      yield put(A.resetApplicationExceptCaseDetailsAndPolicyRecord());
      // yield put(CD.asyncCaseNumber(new Loaded(action.payload.caseNumber)));
      yield put(CD.changeCaseName(response.right?.caseName));
    } else if (response.left.key === 'Unauthorized') {
      yield put(push(ApplicationEntryRouteConstants.AWDLogin));
    } else {
      yield put(CD.changeCaseNumberError(action.payload.caseNumber));
    }
  });
}

function* getManagedStatusForBeneficiaries() {
  yield takeLatest(CD.CaseDetailsActionType.CHECK_CASE_NUMBER_BENEFICIARY, function*(
    action: ReturnType<typeof CD.checkCaseNumberBeneficiary>
  ) {
    const response: AsyncResult<{
      messages:[],
      beneficiaryCaseDetail: {
        caseNumber: string;
        managedStatus: string;       
      };
    }> = yield call(getManagedStatus, action.payload.caseNumber);
    if (E.isRight(response)) {
      yield put(A.resetApplicationExceptCaseDetailsAndPolicyRecord());
      const emp = response.right.beneficiaryCaseDetail.managedStatus.valueOf().toString();
      if (emp === "Employer Managed") {
        yield put(CD.updateManagedStatusFlag(true));
      } else {
        yield put(CD.updateManagedStatusFlag(false));
      }
      yield put(CD.updateManagedStatus(response.right.beneficiaryCaseDetail.managedStatus));      
    } else if (response.left.key === 'Unauthorized') {
      yield put(push(ApplicationEntryRouteConstants.AWDLogin));
    } else {
      yield put(CD.changeCaseNumberError(action.payload.caseNumber));
    }
  });
}

function* getACHInfoSaga() {
  yield takeLatest(CD.CaseDetailsActionType.CHECK_CASE_NUMBER_ACH, function*(
    action: ReturnType<typeof CD.checkACHCaseNumber>
  ) {
    const response: AsyncResult<{
      messages:[],
      achCaseDetail: {
        achIndicator: boolean;
        caseNumber: string; 
        message: string;       
      };
    }> = yield call(getACHIndicator, action.payload.caseNumber);
    if (E.isRight(response)) {
      // yield put(CD.asyncCaseNumber(new Loaded(action.payload.caseNumber)));
      const achInd = response.right.achCaseDetail.achIndicator;
      if (achInd === null){
        yield put(CD.getACHIndicatorValue(false));  
      } else {
        yield put(CD.getACHIndicatorValue(achInd));
      }

    } else if (response.left.key === 'Unauthorized') {
      yield put(push(ApplicationEntryRouteConstants.AWDLogin));
    }
    else if (response.left.key === 'ServerError') { 
      yield put(CD.asyncCaseNumber(new Loaded(action.payload.caseNumber)));
      // yield put(CD.changeCaseNumberError(action.payload.caseNumber));

      let errDetails: [] = response.left.errorDetails?.messages?.filter(
        (message: {[x: string]: string | string[]}) =>
          message['responseSeverity'] == 'ERROR' && message['message'].includes('The message is empty: Case data does not exist')
      );
      if (errDetails?.length > 0) {
        yield put(CD.changeAchError(true));
      } else {
        yield put(CD.changeAchError(false));      
      }
    }
    else {
      yield put(CD.changeCaseNumberError(action.payload.caseNumber));
    }
  });
}

function* getUserAuthorizedInfoSaga() {
  yield takeLatest(CD.CaseDetailsActionType.CHECK_USER_NAME_JSTAMP, function*(
    action: ReturnType<typeof CD.checkUserNameAuthJStamp>
  ) {
    const response: AsyncResult<{authorized: boolean}> = yield call(getUserAuthorizedInfo, action.payload.userName);
    if (E.isRight(response)) {
      yield put(CD.getUserAuthJStamp(response.right.authorized));
    } else if (response.left.key === 'Unauthorized') {
      yield put(push(ApplicationEntryRouteConstants.AWDLogin));
    } else {
      yield put(CD.asyncCaseNumber(response.left));
    }
  });
}

export default function*() {
  yield all([
    assignPolicyNumber(),
    getProductsSaga(),
    addPolicyNumber(),
    getCaseNameSaga(),
    getSalaryDeductionBillingInfoSaga(),
    getACHInfoSaga(),
    getUserAuthorizedInfoSaga(),
    getManagedStatusForBeneficiaries(),
  ]);
}
